import axios from 'axios';
import { toast } from 'react-toastify';
import { hasCookieSessionHint, clearCookieSessionHint } from '../auth/authSessionHint';

// Debounced notice when the server evicts this device (logged in elsewhere).
let lastDeviceRevokeToastAt = 0;
function notifyDeviceRevoked() {
  const now = Date.now();
  if (now - lastDeviceRevokeToastAt < 5000) return;
  lastDeviceRevokeToastAt = now;
  try {
    toast.info('You were signed out because your account was used on another device.');
  } catch (_) { /* toast unavailable outside app shell */ }
}

function resolveApiBaseUrl() {
  // Dev browser: always same-origin /api so webpack proxy works on LAN/mobile (no CORS, no phone→localhost).
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    return '/api';
  }
  const raw = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const trimmed = String(raw).replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
}

// Create Axios instance with base URL
const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  withCredentials: true,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const AUTH_SKIP_REFRESH = [
  '/auth/refresh',
  '/auth/verify-otp',
  '/auth/login',
  '/auth/register',
  '/auth/send-otp',
  '/auth/resend-otp',
  '/auth/phone-login',
  '/auth/otp/send',
  '/auth/logout',
];

function shouldSkipRefresh(url) {
  return AUTH_SKIP_REFRESH.some((path) => String(url || '').includes(path));
}

let isRefreshing = false;
let refreshQueue = [];

function processRefreshQueue(error) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  refreshQueue = [];
}

function clearLegacyAuthStorage() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  localStorage.removeItem('userRole');
  clearCookieSessionHint();
}

function hasRestorableSession() {
  return !!localStorage.getItem('authToken') || hasCookieSessionHint();
}

function handleSessionExpired() {
  clearLegacyAuthStorage();
  window.dispatchEvent(new Event('logout'));

  const path = (typeof window !== 'undefined' && window.location?.pathname) || '';
  const publicPaths = [
    '/',
    '/login',
    '/login/partner',
    '/login-phone',
    '/login-otp',
    '/vehicle-picker',
    '/home-feed',
    '/login-email',
    '/signup',
    '/forgot-password',
  ];
  const isPublic =
    publicPaths.includes(path) ||
    path.startsWith('/catalog') ||
    path.startsWith('/vehicles') ||
    path.startsWith('/passport/public') ||
    path.startsWith('/bridge/');
  if (!isPublic) {
    window.location.href = '/';
  }
}

// Request interceptor to attach JWT token (legacy bearer flow)
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: refresh cookie session on 401, then retry once
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Device limit exceeded elsewhere → this device's session was revoked.
    if (status === 401 && error.response?.data?.code === 'DEVICE_SESSION_REVOKED') {
      notifyDeviceRevoked();
    }

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh &&
      !shouldSkipRefresh(originalRequest.url) &&
      hasRestorableSession()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/auth/refresh', {}, { skipAuthRefresh: true });
        processRefreshQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processRefreshQueue(refreshError);
        if (hasCookieSessionHint()) {
          try {
            await api.post('/auth/logout', {}, { skipAuthRefresh: true });
          } catch (_) {}
        }
        clearCookieSessionHint();
        handleSessionExpired();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (status === 401 && !shouldSkipRefresh(originalRequest?.url)) {
      handleSessionExpired();
    }

    return Promise.reject(error);
  }
);

/**
 * Prefer server message from axios errors (403/401 bodies); fallback to Error.message or a default.
 */
const TECH_ERROR_REWRITES = [
  {
    test: /MRP_E003|cannot exceed MRP|PRICE_ABOVE_MRP|higher than MRP/i,
    rewrite: (raw) => {
      const priceMrp = raw.match(/\((\d+(?:\.\d+)?)\).*?\((\d+(?:\.\d+)?)\)/);
      if (priceMrp) {
        return `Selling price ₹${priceMrp[1]} cannot be higher than MRP ₹${priceMrp[2]}. Please lower the price or increase MRP.`;
      }
      return 'Price cannot be higher than MRP.';
    },
  },
  {
    test: /MRP_E001|MRP is required/i,
    rewrite: () => 'MRP is required. Please add MRP on the product before continuing.',
  },
  {
    test: /POOL_E002|reservation required/i,
    rewrite: () =>
      'This deal item is no longer reserved. Please add it to cart again and checkout within the time limit.',
  },
  {
    test: /POOL_E003|Not enough units in deal pool/i,
    rewrite: () => 'This deal is out of stock. Please try a lower quantity or another offer.',
  },
  {
    test: /Brand, Part Type and Specification/i,
    rewrite: () => 'Product must have Brand, Part Type, and Specification before creating a deal.',
  },
  {
    test: /slug already exists|duplicate.*slug|identifier.*already exists/i,
    rewrite: () => 'A deal with a similar title already exists. Please change the deal title and try again.',
  },
  {
    test: /coupon code.*already|code.*already in use/i,
    rewrite: (raw) => raw,
  },
];

export function getApiCouponFailures(error) {
  const failures = error?.response?.data?.failures || error?.response?.data?.errors;
  if (!Array.isArray(failures)) return [];
  return failures
    .map((f) => (typeof f === 'string' ? { message: f } : f))
    .filter((f) => f && String(f.message || '').trim());
}

export function getApiErrorMessage(error, fallback = 'Something went wrong') {
  const msg = error?.response?.data?.message;
  const raw = (msg && String(msg).trim()) || (error?.message && String(error.message).trim()) || '';
  if (raw) {
    for (const rule of TECH_ERROR_REWRITES) {
      if (rule.test.test(raw)) return rule.rewrite(raw);
    }
    return raw.replace(/^[A-Z]+_E\d+:\s*/i, '');
  }
  return fallback;
}

export default api;
